// Cut-to-size cart lines display quantity as a piece count (e.g. "2"), but
// Shopify's actual cart quantity is a board-foot pricing unit count (e.g.
// 76 = 2 pieces x 38 units/piece). Rather than rewriting the visible input's
// value to the real unit count (which caused a visible flash to "76" before
// the server-rendered response corrected it back to "2"), this intercepts
// the change event before Dawn's own <cart-items>/<cart-drawer-items>
// listener sees it, and calls its updateQuantity() method directly with the
// correct real quantity. That method only ever uses the quantity argument
// passed to it -- it doesn't re-read the input's value -- so the visible
// box never needs to show anything but the piece count.
/**
 * The line's full property set with Pieces updated, or undefined when it
 * cannot be read or already agrees -- in which case the caller sends no
 * properties at all and change.js leaves them untouched.
 *
 * @param {HTMLInputElement} input
 * @param {number} pieceCount
 * @returns {Record<string, string> | undefined}
 */
function buildProperties(input, pieceCount) {
  const raw = input.dataset.lineProperties;
  if (!raw) return undefined;

  let properties;
  try {
    properties = JSON.parse(raw);
  } catch (error) {
    return undefined;
  }

  // Anything but a plain object here would wipe the line's properties.
  if (!properties || typeof properties !== 'object' || Array.isArray(properties)) return undefined;

  // Nothing to correct: skip the field entirely rather than resending a set
  // that is already right.
  if (String(properties.Pieces) === String(pieceCount)) return undefined;

  return { ...properties, Pieces: String(pieceCount) };
}

document.addEventListener(
  'change',
  (event) => {
    const input = event.target;
    if (!input || !input.matches || !input.matches('.quantity__input[data-units-per-piece]')) return;

    const unitsPerPiece = parseFloat(input.dataset.unitsPerPiece);
    if (!unitsPerPiece) return;

    const cartItemsEl = input.closest('cart-items, cart-drawer-items');
    if (!cartItemsEl || typeof cartItemsEl.updateQuantity !== 'function') return;

    event.stopImmediatePropagation();

    const pieceCount = Math.max(1, parseInt(input.value, 10) || 1);
    const realQuantity = pieceCount * unitsPerPiece;
    const line = input.dataset.index;
    const name = document.activeElement ? document.activeElement.getAttribute('name') : null;
    const variantId = input.dataset.quantityVariantId;

    // Keep the hidden updates[] field in step with the piece box, so a native
    // cart form submit made before the section re-renders still posts the real
    // board-foot quantity rather than a stale one.
    if (input.id) {
      const hidden = document.querySelector(`input[data-real-quantity-for="${input.id}"]`);
      if (hidden) hidden.value = String(realQuantity);
    }

    // The visible Pieces property is set once at add-to-cart, so without this
    // it keeps saying 1 after the shopper asks for 2 -- a wrong piece count on
    // the order is worse than none, because it reads as authoritative.
    // change.js REPLACES properties, so the whole set is resent with only
    // Pieces changed. If the set cannot be read, send nothing and let the
    // quantity update proceed alone: a stale count is still better than a line
    // stripped of _UnitsPerPiece, which the checkout guard would reject.
    cartItemsEl.updateQuantity(line, realQuantity, event, name, variantId, buildProperties(input, pieceCount));
  },
  true
);

/**
 * Repair piece counts that drifted without a quantity event firing.
 *
 * Adding the same size twice does not create a second line -- Shopify merges
 * it into the existing one, doubling the quantity while the properties stay as
 * they were on the first add. No change event fires, so the handler above
 * never sees it and the line keeps claiming 1 piece.
 *
 * data-piece-count is Liquid's own quantity / _UnitsPerPiece, computed fresh on
 * every render, so it is the truth to reconcile against. Runs once per page
 * load and only when the two actually disagree, so the normal case costs
 * nothing and there is no way to loop: after the fix the next render agrees.
 */
function reconcilePieceCounts() {
  document.querySelectorAll('.quantity__input[data-units-per-piece][data-piece-count]').forEach((input) => {
    const pieceCount = parseInt(input.dataset.pieceCount, 10);
    const unitsPerPiece = parseFloat(input.dataset.unitsPerPiece);
    if (!Number.isInteger(pieceCount) || pieceCount < 1 || !unitsPerPiece) return;

    const properties = buildProperties(input, pieceCount);
    if (!properties) return;

    const cartItemsEl = input.closest('cart-items, cart-drawer-items');
    if (!cartItemsEl || typeof cartItemsEl.updateQuantity !== 'function') return;

    // Same quantity, corrected properties. updateQuantity only reads
    // currentTarget off the event, to label a performance marker.
    cartItemsEl.updateQuantity(
      input.dataset.index,
      pieceCount * unitsPerPiece,
      { currentTarget: input },
      null,
      input.dataset.quantityVariantId,
      properties
    );
  });
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', reconcilePieceCounts, { once: true });
} else {
  reconcilePieceCounts();
}
